import { queryOptions } from "@tanstack/react-query";

import {
  fetchActivityOfAdmin,
  fetchActivityOfEmployee,
  fetchAllActivityOfAdminByDate,
  fetchAllActivityOfAdmins,
  fetchAllActivityOfEmployeeByDate,
  fetchAllActivityOfEmployees,
  fetchAllAssignedLeadLogsByDate,
  fetchAllAttendanceOfEmployeeByDate,
  fetchAllClosedLeadLogsByDate,
  fetchAllLeaveApplicationsForSA,
  fetchSingleAdmin,
  getAllAdmins,
  getAllEmployeesForSA,
  getCurrentSuperAdmin,
  getEmployeeCheckinsAsSA,
  getEmployeeCheckinsByDate,
  getEmployeesByAdmin,
} from "./superadmin/superadmin-queries";

export const getCurrentSuperAdminQueryOptions = (adminKey: string) =>
  queryOptions({
    queryKey: ["admin"],
    queryFn: () => getCurrentSuperAdmin(adminKey),
    refetchOnWindowFocus: false,
    enabled: !!adminKey,
  });

export const getAllAdminsQueryOptions = (superAdminKey: string) =>
  queryOptions({
    queryKey: ["sa-admins"],
    queryFn: () => getAllAdmins(superAdminKey),
    refetchOnWindowFocus: false,
  });

export const getSingleAdminQueryOptions = (
  superAdminKey: string,
  adminId: string
) =>
  queryOptions({
    queryKey: ["sa-admin", adminId],
    queryFn: () => fetchSingleAdmin(superAdminKey)(adminId),
    refetchOnWindowFocus: false,
  });

export const getAllEmployeesForSAQueryOptions = (superAdminKey: string) =>
  queryOptions({
    queryKey: ["sa-employees"],
    queryFn: () => getAllEmployeesForSA(superAdminKey),
    refetchOnWindowFocus: false,
  });

export const getSingleEmployeeActivityQueryOptions = (
  superAdminKey: string,
  empId: string
) =>
  queryOptions({
    queryKey: ["sa-employee-activity", empId],
    queryFn: () => fetchActivityOfEmployee(superAdminKey)(empId),
    refetchOnWindowFocus: false,
  });

export const getSingleAdminActivityQueryOptions = (
  superAdminKey: string,
  adminId: string
) =>
  queryOptions({
    queryKey: ["sa-admin-activity", adminId],
    queryFn: () => fetchActivityOfAdmin(superAdminKey)(adminId),
    refetchOnWindowFocus: false,
  });

export const getAllAdminsActivityQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date
) =>
  queryOptions({
    queryKey: ["sa-all-admins-activity", startdate, enddate],
    queryFn: () =>
      fetchAllActivityOfAdmins(superAdminKey)({
        startdate,
        enddate,
      }),
    refetchOnWindowFocus: false,
  });

export const getAllEmployeesActivityQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date
) =>
  queryOptions({
    queryKey: ["sa-all-employees-activity", startdate, enddate],
    queryFn: () =>
      fetchAllActivityOfEmployees(superAdminKey)({
        startdate,
        enddate,
      }),
    refetchOnWindowFocus: false,
  });

export const getEmployeeCheckinsAsSAQueryOptions = (
  superAdminKey: string,
  empId: string
) =>
  queryOptions({
    queryKey: ["sa-emp-checkins", empId],
    queryFn: () => getEmployeeCheckinsAsSA(superAdminKey)(empId),
    refetchOnWindowFocus: false,
    enabled: empId !== "",
  });

export const getAllActivitiesOfEmployeeByDateQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date,
  employee: string
) =>
  queryOptions({
    queryKey: ["sa-employees-date-activity", startdate, enddate, employee],
    queryFn: () =>
      fetchAllActivityOfEmployeeByDate(superAdminKey)({
        startdate,
        enddate,
        employee,
      }),
    refetchOnWindowFocus: false,
  });

export const getAllActivitiesOfAdminByDateQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date,
  admin: string
) =>
  queryOptions({
    queryKey: ["sa-admins-date-activity", startdate, enddate, admin],
    queryFn: () =>
      fetchAllActivityOfAdminByDate(superAdminKey)({
        startdate,
        enddate,
        admin,
      }),
    refetchOnWindowFocus: false,
  });

export const getAllAssignedLeadLogsByDateQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date
) =>
  queryOptions({
    queryKey: ["sa-allAssignedLeadLogs-empDate", startdate, enddate],
    queryFn: () =>
      fetchAllAssignedLeadLogsByDate(superAdminKey)({
        startdate,
        enddate,
      }),
    refetchOnWindowFocus: false,
  });

export const getAllClosedLeadLogsByDateQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date
) =>
  queryOptions({
    queryKey: ["sa-allClosedLeadLogs-empDate", startdate, enddate],
    queryFn: () =>
      fetchAllClosedLeadLogsByDate(superAdminKey)({
        startdate,
        enddate,
      }),
    refetchOnWindowFocus: false,
  });

export const getAllAttendancesOfEmployeeByDateQueryOptions = (
  superAdminKey: string,
  startdate: Date,
  enddate: Date,
  employee: string
) =>
  queryOptions({
    queryKey: ["sa-employee-month-attendance", startdate, enddate, employee],
    queryFn: () =>
      fetchAllAttendanceOfEmployeeByDate(superAdminKey)({
        startdate,
        enddate,
        employee,
      }),
    enabled: employee !== "",
    refetchOnWindowFocus: false,
  });

export const fetchAllLeaveApplicationsForSuperAdminQueryOptions = (
  saKey: string,
  data: {
    startdate: Date;
    enddate: Date;
  }
) =>
  queryOptions({
    queryKey: ["sa-leave-applications", data],
    queryFn: () => fetchAllLeaveApplicationsForSA(saKey)(data),
    refetchOnWindowFocus: false,
    retry: 2,
  });

export const getEmployeeCheckinsByDateQueryOptions = (
  superAdminKey: string,
  employeeId: string,
  date: string
) =>
  queryOptions({
    queryKey: ["sa-checkins-by-date", employeeId, date],
    queryFn: () =>
      getEmployeeCheckinsByDate(superAdminKey)({ employeeId, date }),
    refetchOnWindowFocus: false,
    enabled: employeeId !== "" && date !== "",
  });

export const getEmployeesByAdminQueryOptions = (
  superAdminKey: string,
  adminId: string
) =>
  queryOptions({
    queryKey: ["sa-admin-employees", adminId],
    queryFn: () => getEmployeesByAdmin(superAdminKey)(adminId),
    refetchOnWindowFocus: false,
    enabled: !!adminId,
  });
